/**
 * Turn 4: 60s visible clock; timeout path sets timedOut on AdaptiveQuestionResult.
 */
import React, { useMemo, useRef, useState } from 'react';
import { QuizTimer } from '@/features/quiz/components/QuizTimer';
import { useCountdown } from '@/features/quiz/hooks/useCountdown';
import type { AdaptiveQuestionResult } from '../types';
import {
  TIMED_QUESTION_SECONDS,
  buildAdaptiveResult,
  pickTimedQuestion,
  type AdaptiveTurnQuestion,
} from '../adaptiveEngine';
import { AdaptiveQuestionBody } from './DiagnosticQuestion';

export interface TimedQuestionTurnProps {
  excludeQuestionIds?: string[];
  question?: AdaptiveTurnQuestion;
  budgetSeconds?: number;
  commitTurn?: (result: AdaptiveQuestionResult) => void | Promise<void>;
  onComplete: (result: AdaptiveQuestionResult) => void;
}

export const TimedQuestionTurn: React.FC<TimedQuestionTurnProps> = ({
  excludeQuestionIds = [],
  question: questionProp,
  budgetSeconds = TIMED_QUESTION_SECONDS,
  commitTurn,
  onComplete,
}) => {
  // Picked once: the parent's exclude list is a fresh array every render.
  const [picked] = useState(() => pickTimedQuestion(excludeQuestionIds));
  const question = useMemo(() => questionProp ?? picked, [questionProp, picked]);
  const countdown = useCountdown(budgetSeconds);
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const timedOut = choiceId === null && countdown?.expired === true;
  const done = useRef(false);

  const finish = async () => {
    if (done.current) return;
    done.current = true;
    const result = buildAdaptiveResult({
      turnId: 4,
      kind: 'timed_question',
      question,
      choiceId: choiceId ?? '',
      timedOut,
    });
    if (commitTurn) await commitTurn(result);
    onComplete(result);
  };

  return (
    <section data-testid="timed-question-turn" data-turn="4">
      {countdown && choiceId === null && !timedOut && (
        <div className="mb-4 flex justify-end">
          <QuizTimer secondsRemaining={countdown.secondsRemaining} />
        </div>
      )}
      <AdaptiveQuestionBody
        question={question}
        choiceId={choiceId}
        timedOut={timedOut}
        onSubmit={setChoiceId}
        onContinue={() => void finish()}
      />
    </section>
  );
};
